import { darkenColor, increaseColorBrightness } from '../colorUtils';
import { rawColors } from './rawColors';
import storage from '../storage';
import t from '../../i18n/t';
import { AppTheme } from './types';

export const appThemes: AppTheme[] = [
  {
    primary: rawColors.purple,
    primaryLight: rawColors.purpleLight,
    primaryDark: rawColors.darkPurple,
    key: 'Lavender',
    name: t.LAVENDER
  },
  {
    primary: rawColors.flamingo,
    primaryLight: increaseColorBrightness(rawColors.flamingo),
    primaryDark: darkenColor(rawColors.flamingo, -15),
    key: 'Flamingo',
    name: t.FLAMINGO
  },
  {
    primary: rawColors.sapphire,
    primaryLight: increaseColorBrightness(rawColors.sapphire),
    primaryDark: darkenColor(rawColors.sapphire, -15),
    // Shown as Teal since 2026-10-11 (Petr: it isn't blue); saved as Sapphire, as accounts and browsers already hold it.
    key: 'Sapphire',
    name: t.TEAL
  }
  // {
  //   primary: rawColors.tangerine,
  //   primaryLight: increaseColorBrightness(rawColors.tangerine),
  //   primaryDark: darkenColor(rawColors.tangerine, -15),
  //   name: t.TANGERINE
  // }
];

const setCssVariables = (primary: string, primaryLight: string) => {
  const bodyStyle = document.body.style;
  bodyStyle.setProperty('--color-primary', primary);
  bodyStyle.setProperty('--color-primary-light', primaryLight);
};

/**
 * The preset a saved theme is. Matched on its key, or on a name, the key or the shown one, for copies this browser kept
 * before presets had keys; a custom colour is no preset.
 */
export const presetFor = (theme?: Pick<AppTheme, 'key' | 'name'> | null) =>
  theme
    ? appThemes.find(preset =>
        theme.key ? preset.key === theme.key : preset.key === theme.name || preset.name === theme.name
      )
    : undefined;

// This browser's copy, as the preset it is when it is one, so it carries the preset's current key and name.
const savedTheme = storage.getAppTheme();
export const currentTheme = presetFor(savedTheme) ?? savedTheme ?? appThemes[0];
setCssVariables(currentTheme.primary, currentTheme.primaryLight);

// A colour of the student's own, with the lighter and darker shades the app needs made from it.
export const customAppTheme = (primary: string): AppTheme => ({
  primary,
  primaryLight: increaseColorBrightness(primary),
  primaryDark: darkenColor(primary, -15),
  name: t.CUSTOM
});

/**
 * The colour scheme lives on the account since 2026-10-08, as calendar styling does (Petr); this browser keeps a copy
 * the app is drawn with as it starts. On the account it is just a preset's key or a custom colour's hex, as the
 * account's settings have a size limit.
 */
export const encodeColorScheme = (theme: AppTheme) => presetFor(theme)?.key ?? theme.primary;

export const decodeColorScheme = (value?: string | null): AppTheme | null => {
  if (!value) return null;
  const preset = presetFor({ name: value });
  if (preset) return preset;
  return /^#[0-9a-f]{3,8}$/i.test(value) ? customAppTheme(value) : null;
};

// The same colours drawn, whatever either is called, so a renamed preset doesn't reload the page.
const sameScheme = (a: AppTheme, b: AppTheme) => a.primary.toLowerCase() === b.primary.toLowerCase();

/**
 * Draws the app in the account's colour scheme. The app's colours are worked out as it starts, so a scheme other than
 * the one this browser started with is copied here and the page reloaded; with the same one, nothing happens.
 */
export const applyColorScheme = (value?: string | null) => {
  const theme = decodeColorScheme(value);
  if (!theme || sameScheme(theme, currentTheme)) return false;
  storage.setAppTheme(theme);
  window.location.reload();
  return true;
};
